import {useTranslation} from 'react-i18next'
import {useToggle} from 'usehooks-ts'
import {useState} from 'react'

import {Button, DataSheetDrawerContent, DataSheetItem, Slideout, SlideoutBody, SlideoutContainer, SlideoutHeader} from '@sdox/ui'
import {DataSheetLookupEnum} from '@sdox/api'

import {ThemeProvider, useXcTheme} from '../ThemeProvider'
import {Section} from '../Section'

import type {XcNwBlock_BlockDataSheetType_Fragment, DataSheetLookupFragment} from '@sdox/api'

const LIMIT_COLLAPSED = 4

const processLookup = ({lookupType, value}: Omit<DataSheetLookupFragment, '__typename'>, rawData: any) => {
  const isStatic = lookupType === DataSheetLookupEnum.Static
  return isStatic ? value : rawData[value]
}

export const BlockDataSheetType = (props: XcNwBlock_BlockDataSheetType_Fragment) => {
  const {items, sections, rawItems} = props
  const {t} = useTranslation()
  const [isExpanded, toggleIsExpanded] = useToggle(false)
  const {xcTheme} = useXcTheme()

  const [drawerItem, setDrawerItem] = useState<number | false>(false)

  const openDialog = (index: number) => setDrawerItem(index)
  const closeDialog = () => setDrawerItem(false)
  const isOpen = drawerItem !== false

  const limit = isExpanded ? items.length : LIMIT_COLLAPSED

  return (
    <div className={'grid grid-cols-1 gap-block-small @[664px]:grid-cols-2'}>
      {/* filters will be implemented here */}
      {items.slice(0, limit).map((item, index) => (
        <DataSheetItem
          key={index}
          title={item.title}
          mediaProps={{
            src: item.media.thumbnailUrl,
            alt: item.media.altText ?? '',
          }}
          description={item.description ?? undefined}
          additional={item.additional ?? undefined}
          buttonLabel={item.btnLabel}
          onButtonClick={() => openDialog(index)}
        />
      ))}
      <Slideout
        open={isOpen}
        onClose={closeDialog}
      >
        <ThemeProvider
          xcTheme={xcTheme}
          className={'contents'}
        >
          <SlideoutContainer>
            <SlideoutBody>
              <SlideoutHeader
                sticky={true}
                onClose={closeDialog}
              />
              {isOpen ? (
                <Section.Container className={'!mb-spacing-0'}>
                  <Section.Item>
                    <Section.Content>
                      <DataSheetDrawerContent
                        title={items[drawerItem].title}
                        mediaProps={{
                          src: items[drawerItem].media.thumbnailUrl,
                          alt: items[drawerItem].media.altText ?? '',
                        }}
                        additional={items[drawerItem].additional ?? undefined}
                        description={items[drawerItem].description ?? undefined}
                        tableData={sections.map(({sectionName, items}) => ({
                          sectionName,
                          data: items.map(({label, value}) => ({
                            label: processLookup(label, rawItems[drawerItem]),
                            data: processLookup(value, rawItems[drawerItem]),
                          })),
                        }))}
                      />
                    </Section.Content>
                  </Section.Item>
                </Section.Container>
              ) : null}
            </SlideoutBody>
          </SlideoutContainer>
        </ThemeProvider>
      </Slideout>
      {items.length > LIMIT_COLLAPSED ? (
        <div className={'flex justify-center @[664px]:col-span-2'}>
          <Button
            variant={'outline'}
            size={'large'}
            onClick={toggleIsExpanded}
          >
            {isExpanded ? t('common:showLess') : t('common:showMore')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
